import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import {
  AlertTriangle, BadgeCheck, Check, ChevronRight, CircleCheckBig, Cpu, Diamond, Minus, Plus,
  ScanLine, Send, Sparkles, TrendingUp, X, Keyboard, FileUp, Boxes, ArrowRight, Terminal,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../../lib/api';
import { formatUsd } from '../../lib/format';
import { useAuth } from '../../lib/authContext';
import { onboardingPhase } from '../../lib/onboardingPhase';
import { clearClientIdentity } from '../../lib/auth';
import { useOrgFeature } from '../../lib/orgFeatures';
import { fillSerial, isModelMismatch, type ScanBlock } from '../../lib/scan';
import { inputCls } from '../../lib/inputCls';
import { ScanCaptureOverlay, type AcceptedScan } from '../../components/scan/ScanCapture';
import { MakeModelPicker, stripMake } from '../../components/shared/MakeModelPicker';
import { ProbeCommandPanel, type ProbeNotify } from '../../components/ProbeCommandPanel';
import { ProbeSessionSummary } from '../../components/ProbeSessionSummary';
import { isRowMatched, type ProbeIngestRow } from '../../components/ProbeIngestFeed';
import { AddAssetStepper } from '../../components/AddAssetStepper';
import { AddAssetSuccessPanel } from '../../components/AddAssetSuccessPanel';
import { type AddMethod } from '../../lib/addAssetMethod';
import { usePublishAddAssetWizardRail } from '../../lib/addAssetWizardRail';
import { useAddAssetMethodMemory } from '../../lib/useAddAssetMethodMemory';
import {
  computeWizardSteps, wizardStepIdsForMethod, FORM_STEPS, type WizardStepId,
} from '../../lib/addAssetWizardSteps';
import { cn } from '@siliconx/shared/ui/utils';
import { qk } from '../../lib/queryKeys';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { BackLink } from '@siliconx/shared/ui/BackLink';

/** Scroll targets for a click on a *complete* step in AddAssetStepper - 'run'
 * and 'confirm' have no distinct anchor: both only ever become complete
 * once a capture has already succeeded, at which point the stepper is
 * non-interactive (the success panel is showing) and neither is clickable. */
const WIZARD_STEP_SECTION_ID: Partial<Record<WizardStepId, string>> = {
  method: 'step-method',
  build: 'step-build',
  identify: 'step-identify',
  condition: 'step-condition',
};

/** Loose UUID check - just enough to reject garbage before it reaches the
 * API's own `z.string().uuid()` validation on `/api/client/inventory/ingests`. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/assets/submit')({
  // SIL-1208: the ACTIVE INVENTORY TOKEN ROW ID (never the sxinv_ secret) -
  // safe to put in a URL. Round-tripping it lets a reload resume the same
  // TrueRead capture session's readings feed; see ProbeCommandPanel's
  // `resumeTokenId`/`onTokenIdChange`.
  validateSearch: (search: Record<string, unknown>): { probeTokenId?: string } => ({
    probeTokenId:
      typeof search.probeTokenId === 'string' && UUID_RE.test(search.probeTokenId)
        ? search.probeTokenId
        : undefined,
  }),
  component: SubmitAssetPage,
});

interface SubmitPayload {
  assetClass: string;
  make?: string;
  model?: string;
  serialNumber: string;
  serialNumbers: string[];
  condition: string;
  quantity: number;
  notes?: string;
  teamId?: string | null;
  skuId?: string;
  scanIds?: string[];
}

interface Team {
  id: string;
  name: string;
}

// Spreadsheet-friendly parse: newline-separated (Excel column copy) first,
// taking column A when tabs are present; a single line splits on commas.
// Capped so a huge accidental paste can't render thousands of unvirtualized
// rows into the form.
const MAX_PASTED_SERIALS = 200;
function parseSerialPaste(text: string): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => (l.split('\t')[0] ?? '').trim())
    .filter(Boolean);
  const values = lines.length === 1
    ? (lines[0] ?? '').split(',').map((s) => s.trim()).filter(Boolean)
    : lines;
  return values.slice(0, MAX_PASTED_SERIALS);
}

interface ReferenceData {
  assetClasses: { id: string; name: string }[];
  manufacturers: { id: string; name: string }[];
}

interface FmvEstimate {
  matched: boolean;
  sku?: { id: string; make: string | null; model: string | null; label: string };
  median: number | null;
  low: number | null;
  high: number | null;
  sampleSize: number;
  confidence: 'high' | 'medium' | 'low';
  /** SIL-774: true when the matched sku is a master (grain='master') — the
   * range is a family estimate, not this unit's specific configuration. */
  configurationUnknown: boolean;
}

// Condition cards — design labels mapped to the submission enum (new/used/refurbished).
// SIL-1035: "Enterprise Wear" renamed to "Normal Wear" (all org types) to
// match the asset-detail ConditionBadge's "Normal wear" label — the saved
// enum value ('used') is unchanged. Do not touch 'refurbished'/"High Wear"
// (SIL-1038, out of scope).
const CONDITIONS = [
  { value: 'new', label: 'Like New', desc: 'Minimal use, no visible wear' },
  { value: 'used', label: 'Normal Wear', desc: 'Used, fully working, typical signs of use' },
  { value: 'refurbished', label: 'High Wear', desc: 'Heavy use; thermal or cosmetic wear' },
] as const;

const CONFIDENCE_LABEL = { high: 'High', medium: 'Medium', low: 'Low' } as const;

/** SIL-1183: the individual-org capture-success moment - see AddAssetSuccessPanel. */
interface CaptureSuccess {
  headline: string;
  itemLabel: string;
  verifiedByTrueRead: boolean;
}

/** Pure so it's easy to reason about/test independently of component state. */
function hasDuplicateSerial(serials: string[]): boolean {
  const seen = new Set<string>();
  for (const s of serials) {
    const v = s.trim().toLowerCase();
    if (!v) continue;
    if (seen.has(v)) return true;
    seen.add(v);
  }
  return false;
}

function SubmitAssetPage() {
  const navigate = useNavigate();
  // SIL-1208: the token id ProbeCommandPanel resumed from (or just minted) -
  // see the route's `validateSearch` above. Route-scoped (not the bare
  // `useNavigate()` above, used elsewhere for cross-route navigation) so
  // TypeScript knows this route's own search shape. `replace: true` so
  // build/rebuild never grows the back-button history with a probe id.
  const { probeTokenId } = Route.useSearch();
  const navigateSearch = Route.useNavigate();
  const handleProbeTokenIdChange = useCallback(
    (tokenId: string | null) => {
      void navigateSearch({ search: (prev) => ({ ...prev, probeTokenId: tokenId ?? undefined }), replace: true });
    },
    [navigateSearch],
  );
  const { user, organizationType, tosAccepted, canSell, canBuy, hasAssets } = useAuth();
  const isAdmin = user?.role === 'admin';
  // SIL-1182: TrueRead-first three-card picker for individual orgs (no
  // "Also available" row, no CSV/integration). Business keeps today's
  // Manual-first picker with all five methods - unchanged.
  const isIndividual = organizationType === 'individual';
  // SIL-1812: during setup (no asset yet) the setup rail in AppShell shows
  // the wizard steps, so the in-page stepper hides at `lg+` (it still shows on
  // mobile, and after setup).
  const inSetup = onboardingPhase({ tosAccepted, organizationType, canSell, canBuy, hasAssets }) !== 'done';
  const canTeams = useOrgFeature('teams');
  // SIL-1035: an individual customer_org has no bulk-CSV / CMDB-integration
  // import path (bulkImport has no API consumer yet - this is UI-only
  // gating; see this ticket's PR notes) - closes SIL-1030 problem 2.
  const canBulkImport = useOrgFeature('bulkImport');
  // 'probe' has no org-feature gate: Customs (its landing page) is open to
  // both individual and business org types today, so this method stays
  // available for both, unlike the bulkImport-gated csv/integration methods.
  const availableMethods = canBulkImport
    ? METHODS
    : METHODS.filter((m) => m.key === 'manual' || m.key === 'scan' || m.key === 'probe');

  // Add-asset method hub (SIL-212). 'manual' and 'scan' share the manual form
  // body (scan just also opens the capture overlay); 'csv', 'integration',
  // and 'probe' swap the body for their own panels.
  //
  // SIL-1182: individual orgs default to TrueRead, then to whichever method
  // they last completed a capture with (see useAddAssetMethodMemory below).
  // organizationType resolves async, so 'manual' is the render-0 default for
  // everyone and an effect corrects it to the individual's remembered/default
  // method the first time organizationType is known - guarded so it applies
  // exactly once and never clobbers a selection the user already made.
  const [method, setMethod] = useState<AddMethod>('manual');
  const { initialMethod, rememberMethod } = useAddAssetMethodMemory(user?.email ?? null);
  const appliedOrgDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedOrgDefaultRef.current || organizationType == null) return;
    appliedOrgDefaultRef.current = true;
    if (organizationType === 'individual') setMethod(initialMethod);
  }, [organizationType, initialMethod]);

  // SIL-1183: business keeps today's Normal Wear preselection; the wizard
  // (individual orgs) does not preselect a condition - see the effect below
  // and handleSubmit's "Pick a condition." validation. Defaulting to 'used'
  // here (rather than '') avoids a flash of "nothing selected" for the vast
  // majority of first renders (business orgs, and individual orgs before
  // organizationType resolves).
  const [form, setForm] = useState({
    assetClass: '',
    make: '',
    model: '',
    condition: 'used',
    quantity: 1,
    notes: '',
  });
  const conditionTouchedRef = useRef(false);
  const appliedConditionDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedConditionDefaultRef.current || organizationType == null) return;
    appliedConditionDefaultRef.current = true;
    if (organizationType === 'individual' && !conditionTouchedRef.current) {
      setForm((f) => ({ ...f, condition: '' }));
    }
  }, [organizationType]);
  function setCondition(value: string) {
    conditionTouchedRef.current = true;
    set('condition', value);
  }

  // SIL-1183: wizard state - a probe build success (ProbeCommandPanel's
  // onProbeBuilt) and a capture success (a completed manual/scan
  // submission) for individual orgs only.
  const [probeBuilt, setProbeBuilt] = useState(false);
  // SIL-1211: true while the "change method" confirmation is open - only
  // ever opened from the run step (a probe is built, the session isn't done
  // yet), since that's the one moment switching methods silently drops a
  // live capture session (root cause: probeBuilt was never reset when the
  // probe panel unmounted under a method switch).
  const [confirmEndSessionOpen, setConfirmEndSessionOpen] = useState(false);
  const [captureSuccess, setCaptureSuccess] = useState<CaptureSuccess | null>(null);
  // SIL-1195: TrueRead only - true once the user chooses "Done capturing",
  // ending the probe session. Unlike captureSuccess above, a matched
  // reading never sets this on its own (no auto-advance on any reading) -
  // ProbeImportPanel owns its own session-summary swap and only reports
  // this up so the wizard bar's Run & capture / Confirm steps can reflect
  // it (see addAssetWizardSteps.ts).
  const [probeSessionDone, setProbeSessionDone] = useState(false);
  // Admins may route the asset straight to a team; members always get their
  // own team, applied server-side — no picker needed for them.
  const [teamId, setTeamId] = useState('');
  const { data: teams } = useQuery({
    queryKey: qk.teams(),
    queryFn: async () => {
      const res = await api.api.client.teams.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as Team[];
    },
    enabled: isAdmin && canTeams,
  });
  // One serial per unit — length is kept in sync with quantity.
  const [serials, setSerials] = useState<string[]>(['']);
  // Asset class value auto-filled by the last model pick — so the model
  // clear button can undo it without clobbering a manual selection.
  const autoClassRef = useRef<string | null>(null);
  // Catalog SKU id from the last combobox pick — cleared on any free-text
  // edit so a stale pick never rides along with a hand-typed model string.
  const [skuId, setSkuId] = useState<string | null>(null);
  // A6 (SIL-953): asset class resolved by the picker's catalog match. Non-null
  // replaces the user-selected Asset class field and is what the asset gets.
  // Off-catalog (no skuId) has no derived class — that case keeps the manual
  // <select> below.
  const [derivedAssetClass, setDerivedAssetClass] = useState<string | null>(null);
  // Stable identity + same-value guard: the picker reports this from an
  // effect, so an unguarded setState here would re-render forever.
  const handleAssetClassChange = useCallback((c: string | null) => {
    setDerivedAssetClass((prev) => (prev === c ? prev : c));
    if (c) setForm((f) => (f.assetClass === c ? f : { ...f, assetClass: c }));
  }, []);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});
  const [serialErrors, setSerialErrors] = useState<Record<number, string>>({});

  // ---- Scan intake (RUF-500) ----------------------------------------------
  const [scanOpen, setScanOpen] = useState(false);
  // Scans backing the MAIN submission (first scan locks make/model; later
  // matching scans append serials).
  const [scanIds, setScanIds] = useState<string[]>([]);
  // 429/503 from scan endpoints — disables scanning UI, never manual entry.
  const [scanBlock, setScanBlock] = useState<ScanBlock | null>(null);
  // Scan that resolved to a DIFFERENT model — awaiting Keep anyway / Discard.
  const [pendingMismatch, setPendingMismatch] = useState<AcceptedScan | null>(null);
  const [mismatchSerial, setMismatchSerial] = useState('');
  // Kept mismatches each become their own single-unit submission on submit.
  const [keptMismatches, setKeptMismatches] = useState<AcceptedScan[]>([]);
  // The locked model is a new-to-catalog draft (pending ops review).
  const [draftModel, setDraftModel] = useState(false);

  const {
    data: ref, isError: refError, refetch: refetchRef,
  } = useQuery({
    queryKey: qk.reference(),
    queryFn: async () => {
      const res = await api.api.client.reference.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as ReferenceData;
    },
  });
  const manufacturers = ref?.manufacturers ?? [];
  const assetClasses = ref?.assetClasses ?? [];

  // Live FMV — debounce the inputs that drive the estimate, then query.
  // ponytail: inline setTimeout debounce; extract a hook only if reused.
  const [debounced, setDebounced] = useState({ make: '', model: '', condition: 'used' });
  useEffect(() => {
    const id = setTimeout(
      () => setDebounced({ make: form.make, model: form.model, condition: form.condition }),
      300,
    );
    return () => clearTimeout(id);
  }, [form.make, form.model, form.condition]);

  const estimateEnabled = !!(debounced.make.trim() && debounced.model.trim());
  const {
    data: estimate, isFetching: estimateLoading, isError: estimateError, refetch: refetchEstimate,
  } = useQuery({
    queryKey: qk.fmvEstimate(debounced.make, debounced.model, debounced.condition),
    queryFn: async () => {
      const res = await api.api.client['fmv-estimate'].$get({
        query: {
          make: debounced.make.trim(),
          model: debounced.model.trim(),
          condition: debounced.condition,
        },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as FmvEstimate;
    },
    enabled: estimateEnabled,
  });

  const queryClient = useQueryClient();

  // Kept model mismatches split into their own single-unit submissions, so
  // one click can create several — submitted sequentially, main one first.
  const mutation = useMutation({
    mutationFn: async (payloads: SubmitPayload[]) => {
      for (const payload of payloads) {
        const res = await api.api.client.submissions.$post({ json: payload });
        await throwIfNotOk(res);
      }
    },
    onSuccess: (_data, payloads) => {
      // SIL-1182/1183: a manual/scan submission is the capture-success path
      // for those two methods - probe's own success (a matched reading) is
      // handled inside ProbeImportPanel, which never goes through this
      // mutation. Business keeps today's toast + redirect; individual gets
      // the persistent success panel instead (no toast, no navigation).
      if (isIndividual) {
        rememberMethod(method);
        clearClientIdentity();
        void queryClient.invalidateQueries({ queryKey: qk.identity() });
        const first = payloads[0];
        const identity = [first?.make, first?.model].filter(Boolean).join(' ').trim();
        setCaptureSuccess({
          headline: payloads.length > 1 ? `${payloads.length} assets captured.` : 'Asset captured.',
          itemLabel:
            payloads.length > 1
              ? `${payloads.length} assets`
              : identity
                ? `${identity} · ${first?.serialNumber}`
                : `Serial ${first?.serialNumber}`,
          verifiedByTrueRead: false,
        });
        return;
      }
      toast.success(
        payloads.length > 1
          ? `${payloads.length} assets added to your portfolio.`
          : 'Asset added to your portfolio.',
      );
      navigate({ to: '/submissions' });
    },
    // The API returns { error: { code, message } } (submissions.ts) rather
    // than a per-field error list, so there's no index to route a message
    // back onto a specific serial input — routing structured field errors
    // into setErrors/setSerialErrors needs that API shape to change first.
    // Until then: name the known cases in plain, consequence-first language,
    // falling back to the server message for anything else.
    onError: (err: Error & { body?: { error?: { code?: string } } }) => {
      if (err.body?.error?.code === 'invalid_serials') {
        toast.error(`Couldn't submit — ${err.message}`);
        return;
      }
      toast.error(err.message ? `Couldn't submit — ${err.message}` : "Couldn't submit this asset.");
    },
  });

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  // Reducing quantity below the number of filled serials needs confirmation
  // (window.confirm broke the visual system — see pendingQuantity below) —
  // the actual state mutation is applyQuantity, called either immediately
  // or once the in-system dialog is confirmed.
  const [pendingQuantity, setPendingQuantity] = useState<{ q: number; discarded: number } | null>(null);
  // Guard for a serial paste that would clobber already-filled rows —
  // mirrors pendingQuantity's confirm pattern rather than silently
  // overwriting (SIL critique).
  const [pendingSerialPaste, setPendingSerialPaste] = useState<{ i: number; values: string[]; overwritten: number } | null>(null);

  function applyQuantity(q: number) {
    set('quantity', q);
    setSerials((s) => (q > s.length ? [...s, ...Array(q - s.length).fill('')] : s.slice(0, q)));
    setSerialErrors({});
  }

  function setQuantity(raw: string) {
    const q = Math.max(1, parseInt(raw, 10) || 1);
    if (q < serials.length && serials.slice(q).some((s) => s.trim())) {
      setPendingQuantity({ q, discarded: serials.length - q });
      return;
    }
    applyQuantity(q);
  }

  function setSerial(i: number, value: string) {
    setSerials((s) => s.map((v, idx) => (idx === i ? value : v)));
    setSerialErrors(({ [i]: _cleared, ...rest }) => rest);
  }

  function applySerialPaste(i: number, values: string[]) {
    setSerials((s) => s.map((v, idx) => values[idx - i] ?? v));
    setSerialErrors({});
    if (i + values.length > form.quantity) {
      toast.warning(`Pasted ${values.length} serials but quantity is ${form.quantity} — extras were ignored.`);
    }
  }

  // Pasting a multi-value clipboard (spreadsheet column, comma list) into row i
  // fills row i and the following rows, capped at quantity. A row already
  // holding a value gets a confirm first, same as the quantity-reduction
  // guard below, instead of silently overwriting it.
  function handleSerialPaste(i: number, e: React.ClipboardEvent<HTMLInputElement>) {
    const raw = e.clipboardData.getData('text');
    const values = parseSerialPaste(raw);
    if (values.length <= 1) return; // normal single-value paste
    e.preventDefault();
    if (values.length === MAX_PASTED_SERIALS && raw.split(/\r?\n/).length > MAX_PASTED_SERIALS) {
      toast.warning(`Pasted more than ${MAX_PASTED_SERIALS} serials — only the first ${MAX_PASTED_SERIALS} were used.`);
    }
    const overwritten = values.reduce(
      (n, _v, idx) => n + (serials[i + idx]?.trim() ? 1 : 0),
      0,
    );
    if (overwritten > 0) {
      setPendingSerialPaste({ i, values, overwritten });
      return;
    }
    applySerialPaste(i, values);
  }

  // ---- Scan intake handlers (RUF-500) -------------------------------------

  /** Fold an accepted scan into the form: lock identity, fill/append serial. */
  function applyScanToForm(s: AcceptedScan) {
    if (!form.model.trim() && s.model) {
      set('model', s.model);
      setSkuId(s.skuId);
      setDraftModel(s.draft);
      if (s.manufacturer) {
        const known = manufacturers.find(
          (m) => m.name.toLowerCase() === s.manufacturer?.toLowerCase(),
        );
        if (known) set('make', known.name);
      }
    }
    const next = fillSerial(serials, s.serial);
    if (next !== serials) {
      setSerials(next);
      setSerialErrors({});
      if (next.length !== serials.length) set('quantity', next.length);
    }
  }

  function handleScanAccept(s: AcceptedScan) {
    setScanOpen(false);
    if (isModelMismatch(form.model, s.model)) {
      // Different model than the one locked on the form — Keep anyway /
      // Discard, rendered inline under the Identify section.
      setPendingMismatch(s);
      setMismatchSerial(s.serial ?? '');
      return;
    }
    setScanIds((ids) => (ids.includes(s.scanId) ? ids : [...ids, s.scanId]));
    applyScanToForm(s);
  }

  function keepMismatch() {
    if (!pendingMismatch) return;
    const serial = mismatchSerial.trim();
    if (!serial) return;
    setKeptMismatches((k) => [...k, { ...pendingMismatch, serial }]);
    setPendingMismatch(null);
    setMismatchSerial('');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    if (!form.assetClass.trim()) next.assetClass = 'Asset class is required.';
    // SIL-1183: the wizard doesn't preselect a condition (business does, via
    // the effect above, so this never trips for business in practice).
    if (!form.condition) next.condition = 'Pick a condition.';

    const nextSerial: Record<number, string> = {};
    const seen = new Map<string, number>();
    serials.forEach((s, i) => {
      const v = s.trim();
      if (!v) {
        nextSerial[i] = 'Serial number is required.';
        return;
      }
      const key = v.toLowerCase();
      const dupOf = seen.get(key);
      if (dupOf !== undefined) {
        nextSerial[i] = `Duplicate of serial ${dupOf + 1}.`;
      } else {
        seen.set(key, i);
      }
    });

    setErrors(next);
    setSerialErrors(nextSerial);
    if (Object.keys(next).length > 0 || Object.keys(nextSerial).length > 0) return;

    if (pendingMismatch) {
      toast.error('Resolve the scanned-label warning first — keep or discard it.');
      return;
    }

    const trimmed = serials.map((s) => s.trim());
    const teamField = isAdmin ? (teamId || null) : undefined;
    mutation.mutate([
      {
        assetClass: form.assetClass.trim(),
        make: form.make.trim() || undefined,
        model: form.model.trim() || undefined,
        serialNumber: trimmed[0] ?? '',
        serialNumbers: trimmed,
        condition: form.condition,
        quantity: form.quantity,
        notes: form.notes.trim() || undefined,
        teamId: teamField,
        skuId: skuId ?? undefined,
        scanIds: scanIds.length > 0 ? scanIds : undefined,
      },
      // Kept mismatches — each a separate single-unit submission.
      ...keptMismatches.map((m) => ({
        assetClass: form.assetClass.trim(),
        make: m.manufacturer ?? undefined,
        model: m.model ?? undefined,
        serialNumber: m.serial ?? '',
        serialNumbers: [m.serial ?? ''],
        condition: form.condition,
        quantity: 1,
        teamId: teamField,
        skuId: m.skuId ?? undefined,
        scanIds: [m.scanId],
      })),
    ]);
  }

  // SIL-1183: pure step model (lib/addAssetWizardSteps.ts) fed with this
  // page's own completion signals - the bar and the inline StepTag numbers
  // below both read off `wizardSteps`, so they can't drift apart.
  const identifyComplete = !!(
    form.make.trim()
    && form.model.trim()
    && form.assetClass.trim()
    && serials.every((s) => s.trim())
    && !hasDuplicateSerial(serials)
  );
  const conditionSelected = !!form.condition;
  // SIL-1184: one accent focal point per view. The manual/scan form (both
  // org types) has no separate "ready to submit" view of its own - Identify
  // and Condition are both always on screen - so readiness is this single
  // boolean, reused to settle the chosen condition chip and to promote the
  // submit button from neutral to the focal primary action. Individual only
  // - business keeps its pre-selected condition and always-primary submit.
  const formReady = identifyComplete && conditionSelected;
  const wizardStepIds = wizardStepIdsForMethod(method);
  const wizardSteps = useMemo(
    () =>
      computeWizardSteps(wizardStepIds, {
        probeBuilt,
        captureSucceeded: !!captureSuccess,
        probeSessionDone,
        identifyComplete,
        conditionSelected,
      }),
    [wizardStepIds, probeBuilt, captureSuccess, probeSessionDone, identifyComplete, conditionSelected],
  );
  const identifyStepNumber = FORM_STEPS.indexOf('identify') + 1;
  const conditionStepNumber = FORM_STEPS.indexOf('condition') + 1;

  const scrollToWizardStep = useCallback((stepId: WizardStepId) => {
    const targetId = WIZARD_STEP_SECTION_ID[stepId];
    if (!targetId) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(targetId)?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);
  // SIL-1812: hand the same step state to the setup rail in AppShell.
  const wizardInteractive = !captureSuccess && !(method === 'probe' && probeSessionDone);
  usePublishAddAssetWizardRail({
    steps: isIndividual ? wizardSteps : EMPTY_WIZARD_STEPS,
    interactive: wizardInteractive,
    onStepClick: scrollToWizardStep,
  });

  /** "Add another asset" - resets the form and returns to the method view,
   * on the method that just succeeded (which is exactly the "remembered"
   * one - rememberMethod already wrote it in mutation onSuccess / the probe
   * capture-success handler below). */
  function resetForAnotherCapture() {
    setCaptureSuccess(null);
    setProbeBuilt(false);
    setProbeSessionDone(false);
    setForm({ assetClass: '', make: '', model: '', condition: '', quantity: 1, notes: '' });
    conditionTouchedRef.current = false;
    setSerials(['']);
    setSkuId(null);
    setDerivedAssetClass(null);
    setScanIds([]);
    setKeptMismatches([]);
    setDraftModel(false);
    setErrors({});
    setSerialErrors({});
  }

  // SIL-1211: the run step (a probe is built, the session isn't done yet)
  // hides the method cards in favor of a compact "Method: TrueRead" summary
  // - "Change method" opens this confirmation rather than switching right
  // away, since a method switch unmounts the probe panel and would silently
  // strand the live token. Confirming resets the wizard's own build/session
  // bookkeeping AND clears the resumed-session search param - both matter:
  // leaving `probeBuilt` true was the root cause (a rebuild after coming
  // back would look like a "replace" and revoke the still-live token), and
  // leaving `probeTokenId` in the URL would try to resume a session the user
  // just chose to leave.
  function requestEndCaptureSession() {
    setConfirmEndSessionOpen(true);
  }

  function confirmEndCaptureSession() {
    setConfirmEndSessionOpen(false);
    setProbeBuilt(false);
    setProbeSessionDone(false);
    handleProbeTokenIdChange(null);
  }

  return (
    <div className="mx-auto max-w-5xl">
      <BackLink as={Link} to="/assets">Assets</BackLink>
      <PageHeader
        className="mb-5 mt-1"
        title="Add asset"
        subtitle={
          isIndividual
            ? 'Find out what your hardware is worth. Add it in a few minutes and get a verified spec sheet and a market value.'
            : canBulkImport
              ? 'Enter hardware manually, scan a label, or import a CSV/integration batch.'
              : 'Enter hardware manually or scan a label.'
        }
      />

      {/* SIL-1183: sticky 4-step wizard bar, individual orgs only. Stays
          mounted (non-interactive) while the success panel/session summary
          shows, so the bar's own "no step clickable" rule has something to
          point at. SIL-1195: for TrueRead, that's `probeSessionDone`
          ("Done capturing"), not the first matched reading. */}
      {isIndividual && (
        <AddAssetStepper
          steps={wizardSteps}
          interactive={wizardInteractive}
          onStepClick={scrollToWizardStep}
          hideAtDesktop={inSetup}
        />
      )}

      {isIndividual && captureSuccess ? (
        <AddAssetSuccessPanel
          headline={captureSuccess.headline}
          itemLabel={captureSuccess.itemLabel}
          verifiedByTrueRead={captureSuccess.verifiedByTrueRead}
          onAddAnother={resetForAnotherCapture}
        />
      ) : (
        <>
          {/* Method picker (SIL-212) - one place to add hardware by any method.
              SIL-1182: individual orgs get a TrueRead-first three-card row (no
              "Also available", no CSV/integration - see isIndividual above).
              SIL-1035: business, with only one method available, renders the
              manual form directly - a one-option picker is not a picker. */}
          {/* Method picker (SIL-212). SIL-1812: individual orgs see the TrueRead
              panel first and a compact "Other ways" row below it (no 3-card
              grid). SIL-1035: business, with only one method available,
              renders the manual form directly - a one-option picker is not a
              picker. */}
          {!isIndividual && (
            <div id="step-method">
              {availableMethods.length > 1 && (
                <MethodPicker
                  methods={availableMethods}
                  method={method}
                  onSelect={(m) => {
                    setMethod(m);
                    if (m === 'scan') setScanOpen(true);
                  }}
                />
              )}
            </div>
          )}
          {/* SIL-1211: once the probe is built and the capture session is
              still live (step 3, "Run & capture"), a compact summary replaces
              the other-ways row; "Change method" asks for confirmation
              instead of switching straight away (requestEndCaptureSession). */}
          {isIndividual && method === 'probe' && probeBuilt && !probeSessionDone && (
            <div id="step-method">
              <ActiveMethodSummary onChangeMethod={requestEndCaptureSession} />
            </div>
          )}

          {method === 'csv' && <CsvImportPanel onContinue={() => navigate({ to: '/onboarding' })} />}
          {method === 'integration' && <IntegrationImportPanel />}
          {method === 'probe' && (
            <div id="step-build">
              {/* SIL-1195: no auto-advance, no CTA-on-first-match - the panel
                  owns its own "Done capturing" -> session summary swap and
                  behaves identically for individual and business orgs. It
                  only reports back what the sticky wizard bar (individual
                  only) needs: probe-built and session-done. */}
              <ProbeImportPanel
                onProbeBuilt={() => { if (isIndividual) setProbeBuilt(true); }}
                onFirstMatch={() => rememberMethod('probe')}
                onSessionDoneChange={setProbeSessionDone}
                resumeTokenId={probeTokenId ?? null}
                onTokenIdChange={handleProbeTokenIdChange}
              />
            </div>
          )}

          {isIndividual && !(method === 'probe' && probeBuilt && !probeSessionDone) && (
            <div id="step-method">
              <OtherWaysRow
                method={method}
                onSelect={setMethod}
                onActivate={(m) => {
                  if (m === 'scan') setScanOpen(true);
                }}
              />
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className={cn(
              'grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]',
              method !== 'manual' && method !== 'scan' && 'hidden',
            )}
          >
            {/* LEFT - one smart card */}
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              {/* Section 1 - Identify */}
              <section id="step-identify" className="border-b border-border p-6">
                <StepTag n={isIndividual ? identifyStepNumber : 1} neutral={isIndividual}>Identify</StepTag>

            <div className="flex items-center gap-3 rounded-lg border border-border p-3">
              {/* SIL-1184: decorative only - no accent for individual orgs. */}
              <span
                className={cn(
                  'flex h-11 w-11 flex-none items-center justify-center rounded-md',
                  isIndividual ? 'bg-muted text-muted-foreground' : 'bg-brand-50 text-brand-700',
                )}
              >
                <Cpu className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <div className="text-sm font-semibold text-foreground">
                  {form.make || form.model ? `${form.make} ${form.model}`.trim() : 'New hardware asset'}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {estimate?.matched
                    ? estimate.sku?.label
                    : 'Enter make and model to match the catalog'}
                </div>
              </div>
              {estimate?.matched ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                  <BadgeCheck className="h-3.5 w-3.5" /> Catalog match
                </span>
              ) : draftModel ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
                  <Diamond className="h-3 w-3" /> New model — pending review
                </span>
              ) : null}
            </div>

            {/* Scan hardware label CTA (RUF-500) — a 429/503 block disables
                scanning only; manual entry below is unaffected. */}
            <div className="mt-3">
              <button
                type="button"
                disabled={scanBlock?.scope === 'all'}
                onClick={() => setScanOpen(true)}
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-md border px-3.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50',
                  // SIL-1184: outline/secondary for individual orgs - the
                  // Identify view's one accent is the focused input's focus
                  // ring, not this button. Business is unchanged.
                  isIndividual
                    ? 'border-border bg-card text-foreground hover:bg-muted'
                    : 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100',
                )}
              >
                <ScanLine className="h-4 w-4" />
                {scanIds.length > 0 ? 'Scan another serial' : 'Scan hardware label'}
              </button>
              {scanBlock && <p className="mt-1.5 text-xs text-amber-700">{scanBlock.message}</p>}
              {draftModel && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  We don't recognize this model yet — it will be added to the catalog after review.
                </p>
              )}
            </div>

            <MakeModelPicker
              className="mt-4"
              make={form.make}
              model={form.model}
              onMakeChange={(v) => {
                set('make', v);
                setDerivedAssetClass(null);
              }}
              onModelChange={(v) => {
                set('model', v);
                // Free-text edit — a stale skuId must not survive it.
                setSkuId(null);
              }}
              onPick={(m) => {
                // Store the make-stripped model — the make is chosen above
                // and must never reappear in the field. FMV match is by
                // skuId (+ ILIKE substring), so the short model still hits.
                set('model', stripMake(m.modelName, form.make));
                setSkuId(m.skuId);
                // The catalog knows the class — save the user a field.
                if (m.assetClassName) {
                  set('assetClass', m.assetClassName);
                  autoClassRef.current = m.assetClassName;
                }
              }}
              onClear={() => {
                set('model', '');
                setSkuId(null);
                setDerivedAssetClass(null);
                // Undo the auto-filled class, but never a manual choice.
                if (autoClassRef.current && form.assetClass === autoClassRef.current) {
                  set('assetClass', '');
                }
                autoClassRef.current = null;
              }}
              onAssetClassChange={handleAssetClassChange}
            />

            {/* Quantity is pinned to the first column so it never shifts when
                the serial fields switch between inline (qty 1) and the list
                below (qty > 1). Discrete +/- buttons instead of the native
                number spinner — its press-and-hold auto-repeat kept firing
                when the layout moved under the cursor. */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Quantity">
                <div className="flex gap-2">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    disabled={form.quantity <= 1}
                    onClick={() => setQuantity(String(form.quantity - 1))}
                    className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-40"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label="Quantity"
                    className={cn(inputCls(false), 'font-num text-center')}
                    value={form.quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    onKeyDown={(e) => {
                      // Commit the quantity, don't submit the whole form —
                      // Enter here is a natural "done typing" habit.
                      if (e.key === 'Enter') e.preventDefault();
                    }}
                  />
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => setQuantity(String(form.quantity + 1))}
                    className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </Field>
              {form.quantity === 1 && (
                <Field label="Serial number" error={serialErrors[0]} required>
                  <div className="flex gap-2">
                    <input
                      className={cn(inputCls(!!serialErrors[0]), 'font-num')}
                      value={serials[0] ?? ''}
                      onChange={(e) => setSerial(0, e.target.value)}
                      onPaste={(e) => handleSerialPaste(0, e)}
                      placeholder="SN-…"
                    />
                    <button
                      type="button"
                      disabled={scanBlock?.scope === 'all'}
                      title="Scan the hardware label"
                      onClick={() => setScanOpen(true)}
                      className="inline-flex h-10 flex-none items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground"
                    >
                      <ScanLine className="h-4 w-4" /> Scan
                    </button>
                  </div>
                </Field>
              )}
            </div>

            {form.quantity > 1 && (
              <div className="mt-4">
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <label className="block text-xs font-medium text-foreground">
                    Serial numbers — one per unit
                    <span className="ml-0.5 text-red-500">*</span>
                  </label>
                  <span className="font-num flex-none text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {serials.filter((s) => s.trim()).length}
                    </span>{' '}
                    of {form.quantity} entered
                  </span>
                </div>
                <p className="mb-1.5 text-xs text-muted-foreground">
                  Paste a spreadsheet column into any row to fill the rest
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {serials.map((s, i) => {
                    // A row is "valid" once it's non-empty and not flagged as
                    // a duplicate — the only two failure modes checked here.
                    const filled = !!s.trim();
                    const valid = filled && !serialErrors[i];
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className={cn(
                            'flex h-4 w-4 flex-none items-center justify-center rounded-full',
                            valid
                              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                              : 'border border-dashed border-border',
                          )}
                        >
                          {valid && <Check className="h-2.5 w-2.5" />}
                        </span>
                        <div className="flex-1">
                          <input
                            className={cn(inputCls(!!serialErrors[i]), 'font-num')}
                            value={s}
                            onChange={(e) => setSerial(i, e.target.value)}
                            onPaste={(e) => handleSerialPaste(i, e)}
                            placeholder={`Serial ${i + 1} of ${form.quantity}`}
                            aria-label={`Serial number ${i + 1} of ${form.quantity}`}
                          />
                          {serialErrors[i] && <p className="mt-1 text-xs text-red-600">{serialErrors[i]}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Scan resolved to a DIFFERENT model — keep it as its own
                single-unit submission, or discard it (RUF-500). */}
            {pendingMismatch && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3.5">
                <p className="flex items-start gap-2 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                  <span>
                    This label reads{' '}
                    <span className="font-semibold">{pendingMismatch.model}</span> — different from{' '}
                    <span className="font-semibold">{form.model}</span>. Keep it and it will be
                    submitted as its own separate asset.
                  </span>
                </p>
                {!pendingMismatch.serial && (
                  <input
                    className={cn(inputCls(false), 'mt-2.5 font-num')}
                    value={mismatchSerial}
                    onChange={(e) => setMismatchSerial(e.target.value)}
                    placeholder="Serial number for this unit (required to keep)"
                    aria-label="Serial number for the mismatched unit"
                  />
                )}
                <div className="mt-2.5 flex gap-2">
                  <button
                    type="button"
                    disabled={!mismatchSerial.trim()}
                    onClick={keepMismatch}
                    className="inline-flex h-9 items-center rounded-md bg-amber-600 px-3 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    Keep anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPendingMismatch(null);
                      setMismatchSerial('');
                    }}
                    className="inline-flex h-9 items-center rounded-md border border-amber-300 px-3 text-sm font-medium text-amber-800 hover:bg-amber-100"
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}

            {keptMismatches.length > 0 && (
              <div className="mt-4">
                <p className="mb-1.5 text-xs font-medium text-foreground">
                  Will be submitted separately
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {keptMismatches.map((m) => (
                    <span
                      key={m.scanId}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-xs text-foreground"
                    >
                      <span className="font-semibold">{m.model}</span>
                      {m.serial && <span className="font-num text-muted-foreground">{m.serial}</span>}
                      <button
                        type="button"
                        aria-label={`Remove ${m.model ?? 'scanned asset'}`}
                        onClick={() =>
                          setKeptMismatches((k) => k.filter((x) => x.scanId !== m.scanId))
                        }
                        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-muted-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Section 2 — Condition & origin */}
          <section id="step-condition" className="p-6">
            <StepTag n={isIndividual ? conditionStepNumber : 2} neutral={isIndividual}>Condition &amp; origin</StepTag>

            <div role="radiogroup" aria-label="Condition" className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              {CONDITIONS.map((cond) => {
                const on = form.condition === cond.value;
                // SIL-1184: the chosen chip is the accent focal point of the
                // Condition view - but only until the whole form is ready to
                // submit, at which point the focal point moves to the submit
                // button and this chip settles to a check + light neutral
                // tint (no accent border). Business (no "ready" concept -
                // condition is pre-selected) is unchanged: always accented.
                const accented = on && (!isIndividual || !formReady);
                const settled = isIndividual && on && formReady;
                return (
                  <button
                    type="button"
                    key={cond.value}
                    role="radio"
                    aria-checked={on}
                    onClick={() => setCondition(cond.value)}
                    className={cn(
                      'relative rounded-lg border p-3.5 text-left transition-colors',
                      accented
                        ? 'border-brand-600 bg-brand-50'
                        : settled
                          ? 'border-border bg-muted'
                          : 'border-border hover:bg-muted',
                    )}
                  >
                    <div className="text-sm font-semibold text-foreground">{cond.label}</div>
                    <div className="mt-1 text-xs leading-snug text-muted-foreground">{cond.desc}</div>
                    {on && (
                      <span
                        className={cn(
                          'absolute right-3 top-3 flex h-[18px] w-[18px] items-center justify-center rounded-full text-white',
                          accented ? 'bg-brand-600' : 'bg-emerald-600',
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {errors.condition && <p className="mt-1.5 text-xs text-red-600">{errors.condition}</p>}

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* A6 (SIL-953): a catalog match derives the class — the manual
                  select is gone while one is on. While a catalog match is
                  still POSSIBLE (a manufacturer/model is in play but nothing
                  has resolved and nothing off-catalog has been typed yet),
                  render nothing at all rather than a guessable, editable
                  select — same as the prototype, which rendered this slot
                  only once resolved or on a submit error. The select comes
                  back ONLY once the model field holds free text with no
                  catalog match (skuId null, model non-empty) — genuinely
                  off-catalog, the one case A6 never modeled. */}
              {skuId ? (
                <Field label="Asset class" error={derivedAssetClass ? undefined : errors.assetClass} required>
                  <div className={cn(inputCls(false), 'flex items-center justify-between bg-muted')}>
                    <span className="text-foreground">{derivedAssetClass ?? '—'}</span>
                    {derivedAssetClass && <span className="text-xs text-muted-foreground">from catalog match</span>}
                  </div>
                </Field>
              ) : form.model.trim() ? (
                <Field label="Asset class" error={errors.assetClass} required>
                  {refError ? (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      Couldn't load asset classes.
                      <button
                        type="button"
                        onClick={() => void refetchRef()}
                        className="font-semibold underline underline-offset-2"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <select
                      className={inputCls(!!errors.assetClass)}
                      value={form.assetClass}
                      onChange={(e) => set('assetClass', e.target.value)}
                    >
                      <option value="">Select asset class…</option>
                      {assetClasses.map((a) => (
                        <option key={a.id} value={a.name}>{a.name}</option>
                      ))}
                    </select>
                  )}
                </Field>
              ) : (
                errors.assetClass && (
                  <Field label="Asset class" error={errors.assetClass} required>
                    <p className="text-xs text-muted-foreground">Pick a make and model above first.</p>
                  </Field>
                )
              )}
              {/* SIL-1035/SIL-1034: an individual customer_org has no team
                  concept — an admin there would otherwise see an always-
                  empty "Unassigned" select. */}
              {isAdmin && canTeams && (
                <Field label="Team">
                  <select
                    className={inputCls(false)}
                    value={teamId}
                    onChange={(e) => setTeamId(e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {(teams ?? []).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            <details className="group mt-4">
              {/* SIL-1184: decorative only - neutral for individual orgs. */}
              <summary
                className={cn(
                  'inline-flex cursor-pointer list-none items-center gap-1.5 text-sm font-medium',
                  isIndividual ? 'text-foreground' : 'text-brand-700',
                )}
              >
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                Add notes for the appraiser
              </summary>
              <textarea
                className={cn(inputCls(false), 'mt-3 h-auto py-2')}
                rows={3}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="Optional notes about this asset"
              />
            </details>
          </section>
        </div>

        {/* RIGHT — persistent live FMV */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-md">
            {/* SIL-1184: decorative rail header - neutral for individual orgs. */}
            <div className={cn('border-b border-border p-5', isIndividual ? 'bg-muted/40' : 'bg-brand-50/40')}>
              <span
                className={cn(
                  'inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider',
                  isIndividual ? 'text-muted-foreground' : 'text-brand-700',
                )}
              >
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    isIndividual ? 'bg-muted-foreground' : 'bg-brand-600',
                    estimateLoading && 'animate-pulse',
                  )}
                />
                Live estimate
              </span>
              <EstimatePanelBody
                enabled={estimateEnabled}
                loading={estimateLoading}
                estimate={estimate}
                error={estimateError}
                onRetry={() => void refetchEstimate()}
              />
            </div>

            {estimate?.matched && estimate.median !== null && (
              <div className="flex flex-col gap-3 p-5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-muted-foreground">
                    <CircleCheckBig className="h-3.5 w-3.5" /> Confidence
                  </span>
                  <span className="font-num font-semibold text-foreground">
                    {CONFIDENCE_LABEL[estimate.confidence]} ·{' '}
                    {/* PRODUCT.md k-anonymity floor: never show an exact comp
                        count below 5, even on this seller-only pre-submission
                        estimate. */}
                    {estimate.sampleSize < 5 ? 'fewer than 5' : estimate.sampleSize} comps
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-muted-foreground">
                    <TrendingUp className="h-3.5 w-3.5" /> Median FMV
                  </span>
                  <span className="font-num font-semibold text-foreground">{formatUsd(estimate.median)}</span>
                </div>
              </div>
            )}

            <div className="p-5 pt-0">
              {/* SIL-1184: stays neutral until Identify + Condition are both
                  complete, then becomes the focal primary action for
                  individual orgs. Still submits while neutral either way -
                  this only changes the variant, not whether the click
                  fires - so validation errors still show. Business is
                  unchanged (always primary). */}
              <Button
                variant={isIndividual && !formReady ? 'secondary' : 'primary'}
                className="font-semibold gap-2 h-11 inline-flex items-center justify-center w-full"
                type="submit"
                disabled={mutation.isPending}
              >
                <Send className="h-4 w-4" />
                {mutation.isPending ? 'Submitting…' : 'Add asset to portfolio'}
              </Button>
              <p className="mt-2.5 text-center text-xs leading-snug text-muted-foreground">
                Verification runs in the background. You'll get an SX Report &amp; Silicon Passport when it completes.
              </p>
            </div>
          </div>
        </div>
      </form>

      {pendingQuantity && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="discard-serials-title"
        >
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-5 shadow-xl">
            <h3 id="discard-serials-title" className="text-sm font-semibold text-foreground">
              Discard {pendingQuantity.discarded} entered serial{pendingQuantity.discarded === 1 ? '' : 's'}?
            </h3>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Lowering the quantity to {pendingQuantity.q} removes the serial numbers you've already
              typed for the extra unit{pendingQuantity.discarded === 1 ? '' : 's'}.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingQuantity(null)}
                className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
              >
                Keep quantity
              </button>
              <button
                type="button"
                onClick={() => {
                  applyQuantity(pendingQuantity.q);
                  setPendingQuantity(null);
                }}
                className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Discard &amp; reduce
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingSerialPaste && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="overwrite-serials-title"
        >
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-5 shadow-xl">
            <h3 id="overwrite-serials-title" className="text-sm font-semibold text-foreground">
              Overwrite {pendingSerialPaste.overwritten} entered serial{pendingSerialPaste.overwritten === 1 ? '' : 's'}?
            </h3>
            <p className="mt-1.5 text-xs text-muted-foreground">
              This paste replaces serial number{pendingSerialPaste.overwritten === 1 ? '' : 's'} you've already typed.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingSerialPaste(null)}
                className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
              >
                Keep existing
              </button>
              <button
                type="button"
                onClick={() => {
                  applySerialPaste(pendingSerialPaste.i, pendingSerialPaste.values);
                  setPendingSerialPaste(null);
                }}
                className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Overwrite &amp; paste
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SIL-1211: guards a method switch away from a live TrueRead capture
          session - see requestEndCaptureSession/confirmEndCaptureSession.
          SIL-1228: redesign - "Stay on TrueRead" is the visual default and
          keeps initial focus; Escape mirrors it so the user stays capturing
          unless they deliberately click through. */}
      {confirmEndSessionOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="end-capture-session-title"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setConfirmEndSessionOpen(false);
            }
          }}
        >
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h3 id="end-capture-session-title" className="text-base font-semibold leading-snug text-foreground">
                  Change method and end this capture session?
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Machines already captured are safe. Your TrueRead probe stops watching for new readings on this
                  page, and the probe command keeps working wherever it&rsquo;s already pasted - you&rsquo;ll need to
                  come back and build or resume a probe to capture more this way.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={confirmEndCaptureSession}
                className="inline-flex h-9 items-center justify-center rounded-md border border-amber-300 px-3 text-sm font-medium text-amber-800 hover:bg-amber-50"
              >
                End session &amp; change method
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => setConfirmEndSessionOpen(false)}
                className="inline-flex h-9 items-center justify-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Stay on TrueRead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rendered outside the page <form> — the overlay has its own manual-
          entry form and nested forms are invalid HTML. */}
      {scanOpen && (
        <ScanCaptureOverlay
          onClose={() => setScanOpen(false)}
          onAccept={handleScanAccept}
          onBlocked={setScanBlock}
          block={scanBlock}
        />
      )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Method hub (SIL-212)
// ---------------------------------------------------------------------------

const METHODS: { key: AddMethod; label: string; hint: string; icon: typeof Keyboard }[] = [
  { key: 'manual', label: 'Manual entry', hint: 'Type in one asset', icon: Keyboard },
  { key: 'scan', label: 'Scan label', hint: 'Camera / barcode', icon: ScanLine },
  { key: 'csv', label: 'Import CSV', hint: 'Bulk spreadsheet', icon: FileUp },
  { key: 'integration', label: 'From integration', hint: 'ServiceNow CMDB', icon: Boxes },
  { key: 'probe', label: 'TrueRead', hint: 'Run our hardware probe', icon: Terminal },
];

function MethodPicker({
  methods, method, onSelect,
}: {
  methods: typeof METHODS;
  method: AddMethod;
  onSelect: (m: AddMethod) => void;
}) {
  // 'scan' shares the manual body, so highlight Manual while the scan overlay is up.
  const active = method === 'scan' ? 'scan' : method;
  // SIL-add-asset-critique-fixes (P0): the 5-way flat grid gave every method
  // equal weight with no default, forcing a blind choice before the user's
  // first productive action. Manual is the baseline case for every org and
  // gets a single emphasized primary card; the remaining methods stay
  // immediately visible (not behind a click-to-reveal) as a secondary row -
  // demoted in weight, never in visibility.
  const primary = methods.find((m) => m.key === 'manual');
  const secondary = methods.filter((m) => m.key !== 'manual');

  return (
    <div
      role="radiogroup"
      aria-label="Add asset method"
      className="mb-6 flex flex-col gap-3"
    >
      {primary && (
        <MethodOption
          methodKey={primary.key}
          label={primary.label}
          hint={primary.hint}
          icon={primary.icon}
          selected={active === primary.key}
          onSelect={onSelect}
          variant="primary"
        />
      )}
      {secondary.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Also available
          </p>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {secondary.map((m) => (
              <MethodOption
                key={m.key}
                methodKey={m.key}
                label={m.label}
                hint={m.hint}
                icon={m.icon}
                selected={active === m.key}
                onSelect={onSelect}
                variant="secondary"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MethodOption({
  methodKey, label, hint, icon: Icon, selected, onSelect, variant,
}: {
  methodKey: AddMethod;
  label: string;
  hint: string;
  icon: (typeof METHODS)[number]['icon'];
  selected: boolean;
  onSelect: (m: AddMethod) => void;
  variant: 'primary' | 'secondary';
}) {
  const primary = variant === 'primary';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(methodKey)}
      className={cn(
        'flex items-start gap-3 rounded-xl border text-left transition',
        primary ? 'p-4' : 'flex-col gap-2 p-3.5',
        selected
          ? 'border-brand-400 bg-brand-50 ring-1 ring-brand-200'
          : 'border-border bg-card hover:border-border hover:bg-muted',
      )}
    >
      <span
        className={cn(
          'flex flex-none items-center justify-center rounded-md',
          primary ? 'h-11 w-11' : 'h-9 w-9',
          selected ? 'bg-brand-100 text-brand-700' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className={primary ? 'h-5 w-5' : 'h-4 w-4'} />
      </span>
      <span className={cn(primary ? 'flex flex-col gap-0.5' : 'flex flex-col gap-0.5')}>
        <span className={cn('font-semibold text-foreground', primary ? 'text-sm' : 'text-xs')}>
          {label}
        </span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// "Other ways to add an asset" row (SIL-1812, individual orgs only)
// ---------------------------------------------------------------------------

const EMPTY_WIZARD_STEPS: ReturnType<typeof computeWizardSteps> = [];

const OTHER_WAY_LABEL: Partial<Record<AddMethod, string>> = {
  probe: 'Use the TrueRead probe',
  scan: 'Scan a label',
  manual: 'Enter it manually',
};

/**
 * One compact row of plain buttons below the TrueRead panel (replaces the
 * SIL-1182 three-card picker). TrueRead is the default and renders first; this
 * row lists the other two methods. While Scan or Manual is the active method
 * the row also offers a way back to TrueRead. `onSelect` switches the method;
 * `onActivate` carries the one side effect (Scan opens the capture overlay).
 */
function OtherWaysRow({
  method, onSelect, onActivate,
}: {
  method: AddMethod;
  onSelect: (m: AddMethod) => void;
  onActivate: (m: AddMethod) => void;
}) {
  const order: readonly AddMethod[] = method === 'probe' ? ['scan', 'manual'] : ['probe', method === 'scan' ? 'manual' : 'scan'];
  return (
    <div className="mb-4 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
      <span className="text-muted-foreground">Other ways to add an asset:</span>
      {order.map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => {
            onSelect(key);
            onActivate(key);
          }}
          className="font-medium text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
        >
          {OTHER_WAY_LABEL[key]}
        </button>
      ))}
    </div>
  );
}

/**
 * SIL-1211: replaces the three method cards once the run step is live (a
 * probe is built, the capture session isn't done yet) - step 3 shows run
 * content only, so the full picker isn't in view here. "Change method"
 * doesn't jump straight to a different card; it opens a confirmation first
 * (see EndCaptureSessionDialog usage / requestEndCaptureSession above),
 * since switching away silently used to strand the built token.
 */
function ActiveMethodSummary({ onChangeMethod }: { onChangeMethod: () => void }) {
  return (
    <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3.5">
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
        <Terminal className="h-4 w-4 text-brand-700" /> Method: TrueRead
      </span>
      <button
        type="button"
        onClick={onChangeMethod}
        className="text-sm font-medium text-brand-700 hover:underline"
      >
        Change method
      </button>
    </div>
  );
}

function CsvImportPanel({ onContinue }: { onContinue: () => void }) {
  return (
    <Card className="max-w-2xl">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-brand-50 text-brand-700">
          <FileUp className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-semibold text-foreground">Import assets from a CSV</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Download the template, fill one row per asset, upload, and review before committing.
          </div>
        </div>
      </div>
      <Button className="font-semibold gap-2 h-10 inline-flex items-center mt-5"
        onClick={onContinue}
      >
        Continue to CSV import <ArrowRight className="h-4 w-4" />
      </Button>
    </Card>
  );
}

// ponytail: real CMDB pull is SIL-208 (SN-2); this stays a disabled "not yet
// available" state until that lands, rather than a fabricated import.
function IntegrationImportPanel() {
  return (
    <Card className="max-w-2xl">
      <div className="flex items-center gap-3">
        <img src="/service-logos/servicenow.svg" alt="" className="h-11 w-11 flex-none rounded-md border border-border p-1.5 opacity-50" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-foreground">ServiceNow CMDB</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            CMDB import isn't available yet. Use CSV import or manual entry for now.
          </div>
        </div>
        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          Not yet available
        </span>
      </div>
      <Button className="font-semibold gap-2 h-10 inline-flex items-center mt-5" disabled>
        Connect ServiceNow
      </Button>
    </Card>
  );
}

// Wraps the shared probe panel (SIL-695/710) with Add-Asset framing. Hardware
// TrueRead can match automatically (an existing catalog SKU with a price) is
// added as an asset right away; only hardware it can't confidently match
// lands in Customs for manual review. `ProbeIngestFeed` (rendered inside
// `ProbeCommandPanel`) reports per-reading which case applied, and
// `onRowsChange`/`onMatchesChange` bubble that up.
//
// SIL-1195: the probe is reusable across many machines, so this panel never
// auto-advances or shows a CTA on a match - it stays showing the live feed
// (step 3, a dwell state) until the user explicitly clicks "Done capturing",
// at which point it swaps in `ProbeSessionSummary` (step 4) in place.
// Identical for individual and business orgs - this IS "the business
// ProbeSuccessCta path updated to match" the ticket calls for; the old
// business-only inline CTA shown on first match is retired.
function ProbeImportPanel({
  onProbeBuilt,
  onFirstMatch,
  onSessionDoneChange,
  resumeTokenId,
  onTokenIdChange,
}: {
  onProbeBuilt?: () => void;
  /** Fires once, the first time any reading matches - purely a "remember this method" signal, not a capture-success/CTA trigger (SIL-1195 overrides that part of SIL-1183). */
  onFirstMatch?: () => void;
  /** Reports the "Done capturing" toggle up so the sticky wizard bar (individual orgs) can reflect it. */
  onSessionDoneChange?: (done: boolean) => void;
  /** SIL-1208: forwarded to `ProbeCommandPanel` - the `?probeTokenId=` search param this page resumes from on a reload. */
  resumeTokenId?: string | null;
  /** SIL-1208: forwarded to `ProbeCommandPanel` - mirrors the active token id into the URL. */
  onTokenIdChange?: (tokenId: string | null) => void;
}) {
  const notify: ProbeNotify = useCallback(
    (message, type) => (type === 'success' ? toast.success(message) : toast.error(message)),
    [],
  );
  const [matches, setMatches] = useState<ProbeIngestRow[]>([]);
  const [rows, setRows] = useState<ProbeIngestRow[]>([]);
  const onMatchesChange = useCallback((m: ProbeIngestRow[]) => setMatches(m), []);
  const onRowsChange = useCallback((r: ProbeIngestRow[]) => setRows(r), []);
  const anyMatched = matches.length > 0;
  // SIL-1207: any reading that created or resolved an asset - not just a
  // confident catalog match (`matches`/`isRowMatched`) - flips `hasAssets` on
  // the DB-derived identity. A Customs-only capture (status `imported_customs`)
  // still creates the quarantined asset and sets `resolvedAssetId`, so it must
  // invalidate too - otherwise the memoized identity keeps `hasAssets: false`
  // and AddAssetGate bounces "View in portfolio" straight back to this wizard.
  const anyResolved = rows.some((r) => !!r.resolvedAssetId);
  // SIL-1211: true once a probe exists for this session (a fresh build, or a
  // `resumeTokenId` reload) - drives the always-present finish action below.
  // Seeded from `resumeTokenId` (not just the `onProbeBuilt` callback) so a
  // reload lands with the finish action already showing, matching
  // ProbeCommandPanel's own `hasSession`.
  const [built, setBuilt] = useState(() => !!resumeTokenId);
  const handleProbeBuilt = useCallback(() => {
    setBuilt(true);
    onProbeBuilt?.();
  }, [onProbeBuilt]);
  const [sessionDone, setSessionDone] = useState(false);
  // SIL-1206: "Done capturing" no longer swaps straight to the summary off
  // whatever `rows` last held from polling (up to PROBE_INGEST_POLL_MS
  // stale) - it forces one more fetch of the same query ProbeIngestFeed
  // polls (same query key, so this is a genuine refetch, not a second
  // source of truth) and stays on the live feed, button disabled, until
  // that resolves - only then does the summary (with its totals tiles)
  // mount, already showing the settled numbers.
  const [finalizing, setFinalizing] = useState(false);
  const queryClient = useQueryClient();
  // 929/SIL-1207: a reading just landed - re-fetch the identity so the "add a
  // serial" prompt in ProbeSessionSummary counts the assets it just created,
  // and so hasAssets/canSell/canBuy reflect it before the Confirm screen's
  // CTAs render (the identity load is memoized per page session, hence the
  // explicit clear).
  useEffect(() => {
    if (!anyResolved) return;
    clearClientIdentity();
    void queryClient.invalidateQueries({ queryKey: qk.identity() });
  }, [anyResolved, queryClient]);
  // SIL-1182: remember the method on the first match - independent of when
  // (or whether) the user ends the session; unrelated to SIL-1195's
  // no-auto-advance rule, which is only about the wizard/CTA.
  const firstMatchFiredRef = useRef(false);
  useEffect(() => {
    if (!anyMatched || firstMatchFiredRef.current) return;
    firstMatchFiredRef.current = true;
    onFirstMatch?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per mount; onFirstMatch is a fresh closure every render
  }, [anyMatched]);

  // SIL-1206. `rows[0]` always exists here: "Done capturing" only renders
  // (below) once `rows.length > 0`, and every row on this panel carries the
  // one active token's id (ProbeIngestFeed's tokenId prop / server-side
  // filter). Reads straight off the query cache with the SAME key
  // ProbeIngestFeed's own useQuery uses, so this is a genuine refetch of
  // that query, not a second, divergent fetch.
  //
  // SIL-1289/SIL-1812: finishing never blocks or navigates - the asset save
  // already happened as each reading resolved, and profile completion is
  // deferred to the Exchange pages, so this is a local state flip.
  async function finishSession() {
    setFinalizing(true);
    const tokenId = rows[0]?.inventoryTokenId;
    if (tokenId) {
      await queryClient.refetchQueries({ queryKey: qk.inventory.ingestsForToken(tokenId), exact: true });
      const fresh = queryClient.getQueryData<ProbeIngestRow[]>(qk.inventory.ingestsForToken(tokenId));
      if (fresh) {
        setRows(fresh);
        setMatches(fresh.filter(isRowMatched));
      }
    }
    setSessionDone(true);
    onSessionDoneChange?.(true);
  }

  // SIL-1211: the Confirm view's way back to the run step - the token is
  // still active (finishing never revokes it), so this just re-shows the
  // live feed/finish action rather than re-minting anything.
  function backToCapture() {
    setSessionDone(false);
    onSessionDoneChange?.(false);
  }

  if (sessionDone) {
    return (
      <ProbeSessionSummary rows={rows} onBackToCapture={backToCapture} />
    );
  }

  // SIL-1211: names the action and the count, pluralized - secondary until a
  // reading lands, then primary. Always present once a probe exists (not
  // gated on `rows.length > 0`) so step 3 has exactly "one clear finish
  // action" the whole time the session is live, per the ticket.
  const capturedCount = rows.length;
  const finishLabel = finalizing
    ? 'Finishing…'
    : capturedCount === 0
      ? 'Finish and review'
      : `Finish and review — ${capturedCount} machine${capturedCount === 1 ? '' : 's'} captured`;

  return (
    // SIL-1194: no max-width here - the probe panel fills the #step-build
    // container (the page's max-w-5xl) instead of leaving a third of it
    // empty. SIL-1213: ProbeCommandPanel itself is single-column, sequential
    // (How this works -> build) pre-build now, not a side-by-side split.
    <div className="space-y-3">
      <ProbeCommandPanel
        notify={notify}
        showTokenManagement={false}
        onProbeBuilt={handleProbeBuilt}
        onMatchesChange={onMatchesChange}
        onRowsChange={onRowsChange}
        resumeTokenId={resumeTokenId}
        onTokenIdChange={onTokenIdChange}
        hideBuildContextOnceMinted
      />
      {built && (
        <div className="flex justify-end">
          <Button
            variant={capturedCount > 0 ? 'primary' : 'secondary'}
            onClick={() => void finishSession()}
            disabled={finalizing}
            className="font-semibold"
          >
            {finishLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

function EstimatePanelBody({
  enabled, loading, estimate, error, onRetry,
}: {
  enabled: boolean;
  loading: boolean;
  estimate: FmvEstimate | undefined;
  error?: boolean;
  onRetry?: () => void;
}) {
  if (!enabled) {
    return <p className="mt-3 text-sm text-muted-foreground">Enter make and model to see a live FMV estimate.</p>;
  }
  if (error) {
    return (
      <p className="mt-3 flex items-center gap-2 text-sm text-red-700">
        Couldn't load the estimate.
        {onRetry && (
          <button type="button" onClick={onRetry} className="font-semibold underline underline-offset-2">
            Retry
          </button>
        )}
      </p>
    );
  }
  if (loading && !estimate) {
    return <div className="mt-3 h-9 w-32 animate-pulse rounded bg-muted" />;
  }
  if (!estimate?.matched) {
    return (
      <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
        <Sparkles className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" />
        No catalog match yet — we'll appraise this unit after you submit.
      </p>
    );
  }
  // SIL-774: a master-attached match (no variant picked) — median is
  // always null (a master never gets a point estimate), but the family
  // range still renders, flagged distinctly from "no match"/"no pricing".
  if (estimate.configurationUnknown) {
    const { low, high } = estimate;
    if (low === null && high === null) {
      return (
        <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
          <Sparkles className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" />
          FMV not available — we'll appraise this unit after you submit.
        </p>
      );
    }
    return (
      <>
        <div className="mt-2.5 flex items-center gap-2">
          <span className="font-num text-2xl font-bold leading-none tracking-tight text-foreground">
            {formatUsd(low)} – {formatUsd(high)}
          </span>
          <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
            Configuration unknown
          </span>
        </div>
        <div className="mt-1.5 text-xs text-muted-foreground">
          Family price range — pick a specific configuration above for a precise estimate.
        </div>
      </>
    );
  }
  // Catalog match without market pricing (e.g. an unpriced model picked
  // from the suggest dropdown) — distinct from "no match".
  if (estimate.median === null) {
    return (
      <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
        <Sparkles className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" />
        FMV not available — we'll appraise this unit after you submit.
      </p>
    );
  }

  const { low, high, median } = estimate;
  const pct =
    low != null && high != null && median != null && high > low
      ? Math.min(100, Math.max(0, ((median - low) / (high - low)) * 100))
      : 50;

  return (
    <>
      <div className="mt-2.5 font-num text-4xl font-bold leading-none tracking-tight text-foreground">
        {formatUsd(median)}
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground">Median FMV · updates as you edit</div>
      <div className="relative mt-4 h-2 rounded-full bg-muted">
        <span className="absolute inset-y-0 rounded-full bg-brand-200" style={{ left: '12%', right: '12%' }} />
        <span
          className="absolute -top-1 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-white bg-brand-600"
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex justify-between font-num text-xs text-muted-foreground">
        <span>{formatUsd(low)}</span>
        <span>{formatUsd(high)}</span>
      </div>
    </>
  );
}

function StepTag({
  n, neutral, children,
}: { n: number; neutral?: boolean; children: React.ReactNode }) {
  return (
    <div className="mb-3.5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {/* SIL-1184: decorative number chip - individual orgs get the sticky
          stepper above for progress, so this drops the accent; business
          (no stepper) keeps it. */}
      <span
        className={cn(
          'flex h-[18px] w-[18px] items-center justify-center rounded-full font-num text-[13px]',
          neutral ? 'bg-muted text-muted-foreground' : 'bg-brand-50 text-brand-700',
        )}
      >
        {n}
      </span>
      {children}
    </div>
  );
}

function Field({
  label, error, required, children,
}: { label: string; error?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-foreground">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
